import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import AuthShell from "../../components/AuthShell/AuthShell";
import TextField from "../../components/primitives/TextField";
import PasswordField from "../../components/primitives/PasswordField";
import TextButton from "../../components/primitives/TextButton";
import FormError from "../../components/primitives/FormError";
import { ApiError } from "../../services/api";
import { useAuth } from "../../services/AuthContext";
import "./auth.css";

const looksLikeEmail = (s) => /^\S+@\S+\.\S+$/.test(String(s ?? "").trim());

function SignUpPage() {
    const navigate = useNavigate();
    const { user, register } = useAuth();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [name, setName] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [serverErr, setServerErr] = useState(null);
    const emailRef = useRef(null);

    useEffect(() => {
        if (user) navigate("/dashboard", { replace: true });
        emailRef.current?.focus?.();
    }, [user, navigate]);

    const validate = () => {
        const errs = {};
        if (!looksLikeEmail(email)) errs.email = "Enter a valid email address.";
        if (!password || password.length < 8)
            errs.password = "At least 8 characters.";
        else if (password.length > 128)
            errs.password = "Up to 128 characters.";
        return errs;
    };

    const submit = async (e) => {
        e?.preventDefault?.();
        setServerErr(null);
        const errs = validate();
        setErrors(errs);
        if (Object.keys(errs).length) return;

        setSubmitting(true);
        try {
            const body = { email: email.trim(), password };
            if (name.trim()) body.name = name.trim();
            await register(body);
            navigate("/auth/check-email", {
                state: { email: email.trim(), intent: "verify" },
                replace: true,
            });
        } catch (err) {
            const code = err instanceof ApiError ? err.code : null;
            if (code === "account_exists") {
                setServerErr(
                    "An account with this email already exists. Try signing in with Google, or use forgot password to set a password."
                );
            } else if (code === "validation_error" && err.details) {
                const map = {};
                err.details.forEach((d) => {
                    if (d.field && d.message) map[d.field] = d.message;
                });
                if (Object.keys(map).length) setErrors(map);
                else setServerErr(err.message || "Validation error.");
            } else if (code === "too_many_requests") {
                setServerErr("Too many attempts. Try again in a minute.");
            } else {
                setServerErr(
                    err instanceof ApiError
                        ? err.message
                        : err?.message || "Sign-up failed."
                );
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AuthShell
            eyebrow="Console / Sign-up"
            title="Create your account."
            lede="Operator access — manifests, SKU catalog, 3D viewport."
            footer={
                <>
                    <span>Already have one?</span>
                    <Link to="/login" className="auth-shell-link auth-shell-link--accent">
                        Sign in →
                    </Link>
                </>
            }
        >
            <form className="auth-form" onSubmit={submit} noValidate>
                <TextField
                    ref={emailRef}
                    type="email"
                    label="Email"
                    required
                    value={email}
                    onChange={(v) => {
                        setEmail(v);
                        if (errors.email) setErrors((e) => ({ ...e, email: undefined }));
                    }}
                    placeholder="you@company.com"
                    autoComplete="email"
                    error={errors.email}
                />
                <PasswordField
                    label="Password"
                    required
                    value={password}
                    onChange={(v) => {
                        setPassword(v);
                        if (errors.password)
                            setErrors((e) => ({ ...e, password: undefined }));
                    }}
                    placeholder="At least 8 characters"
                    autoComplete="new-password"
                    hint={errors.password ? undefined : "At least 8 characters."}
                    error={errors.password}
                />
                <TextField
                    label="Name"
                    value={name}
                    onChange={setName}
                    placeholder="Optional"
                    autoComplete="name"
                    maxLength={128}
                    hint="How should we address you? Skip if you'd rather not say."
                />

                {serverErr && <FormError>{serverErr}</FormError>}

                <TextButton
                    type="submit"
                    variant="primary"
                    size="lg"
                    fullWidth
                    loading={submitting}
                    iconRight={ArrowRight}
                >
                    Create account
                </TextButton>
            </form>
        </AuthShell>
    );
}

export default SignUpPage;
